import { ActionContext, ActionTree } from 'vuex';
import * as types from './types'
import api from '@reserve/api'
import { EnumCalendarViewType } from '../types';
import { getResourceColor } from './consts'
import reserveHelper from '@reserve/reserve-helper';
import { 
    IEDate, 
    uGetDateFormat, 
    uGetDaysInMonth, 
    uGetWeekStartDate, 
    uGetWeekEndDate,
    uGetMonthStartDate,
    uGetMonthEndDate,
    uGetIsInDate
} from '@appbase/util';

//캘린더 뷰 타입에 따른 시작날짜를 불러옵니다
const getCurrentStartDate = function (date: Date, mdate: Date, viewType: EnumCalendarViewType, action: ActionContext<reserve.state.ReserveState, RootState>) {
    switch (viewType) {
        case EnumCalendarViewType.Daily:
        case EnumCalendarViewType.Merge:
            return date;
        case EnumCalendarViewType.Week:
            return uGetWeekStartDate(date, action.state.calendarSetting.firstDay);
        case EnumCalendarViewType.Month:
            return uGetDaysInMonth(mdate, true, action.state.calendarSetting.firstDay)[0];
    }   
}
//캘린더 뷰 타입에 따른 끝날짜를 불러옵니다
const getCurrentEndDate = function (date: Date, mdate: Date, viewType: EnumCalendarViewType, action: ActionContext<reserve.state.ReserveState, RootState>) {
    switch (viewType) {
        case EnumCalendarViewType.Daily:
        case EnumCalendarViewType.Merge:
            return date;
        case EnumCalendarViewType.Week:
            return uGetWeekEndDate(date, action.state.calendarSetting.firstDay);
        case EnumCalendarViewType.Month:
            var arr = uGetDaysInMonth(mdate, true, action.state.calendarSetting.firstDay);
            return arr[arr.length - 1];
    }  
}

const sortFn = function (a: any, b: any): number {
    return a == b ? 0 : a < b ? -1 : 1;
}

const isValidReloadFromChangedDate = function (targetDate: reserve.DateItem, state: reserve.state.ReserveState, viewType?: EnumCalendarViewType): boolean {
    let flag: boolean = false;

    const backDate: Date = state.backupDate.date;
    const curDate: Date = targetDate.date;
    const vmode: EnumCalendarViewType = viewType || state.calendarMode;    

    switch (vmode) {
        case EnumCalendarViewType.Month:
            let mStart = uGetMonthStartDate(backDate);
            let mEnd = uGetMonthEndDate(backDate);        
            flag = uGetIsInDate(curDate, mStart, mEnd);
            break;
        case EnumCalendarViewType.Week:
            let wStart = uGetWeekStartDate(backDate, state.calendarSetting.firstDay);
            let wEnd = uGetWeekEndDate(backDate, state.calendarSetting.firstDay);
            flag = uGetIsInDate(curDate, wStart, wEnd);
            break;
        case EnumCalendarViewType.Daily:
        case EnumCalendarViewType.Merge:
            flag = uGetIsInDate(curDate, backDate, backDate);
            break;
    }

    return flag;
}

const linkDbEventToResource = function(
    resources: reserve.ResourceItemBase[],
    events: reserve.ItemList<reserve.CalendarItem>, 
    result: any[]): reserve.ItemList<reserve.CalendarItem> {    

    for(var event of result) {
        const existID = events.find((e) => event.id == e.id && (event.appointmentType != 'Occurrence' && event.appointmentType != 'Exception'));

        if(existID) continue;

        var resource = resources.find(r => r.folderId === event.parentFolderID.toString());        

        if(resource) {
            event.color = resource.colorCode == null ? getResourceColor(resource.uniqueId) : resource.colorCode ;
            event.resourceUpn = resource.address;
            event.itemStore = resource.itemStore;
            events.push(event);
        }        
    }
    
    return events;
}

const linkEwsEventToResource = function(
    resources: reserve.ResourceItemBase[],
    events: reserve.ItemList<reserve.CalendarItem>, 
    result: any[]): reserve.ItemList<reserve.CalendarItem> {
    
    for(var event of result) {
        const existID = events.find((e) => event.id == e.id && (event.appointmentType != 'Occurrence' && event.appointmentType != 'Exception'));

        if(existID) continue;

        var resource = resources.find(r => r.folderId === event.parentFolderId);

        if(resource) {
            event.color = resource.colorCode == null ? getResourceColor(resource.uniqueId) : resource.colorCode ;
            event.resourceUpn = resource.address;
            event.itemStore = resource.itemStore;
            events.push(event);
        }    
    }
    
    return events;
}

export default <ActionTree<reserve.state.ReserveState, RootState>>{
    [types.CALENDAR_VIEWMODE](action, viewMode: EnumCalendarViewType) {
        action.commit(types.CALENDAR_VIEWMODE, viewMode);
    },
    
    [types.MINI_CALENDAR_MONTH_MATRIX_LOAD](action) {
        action.commit(types.MINI_CALENDAR_MONTH_MATRIX_LOAD);
    },

    [types.CALENDAR_TOGGLE_RIGHTPANE](action, payload) {
        action.commit(types.CALENDAR_TOGGLE_RIGHTPANE, payload);
    },

    [types.CALENDAR_TOGGLE_LEFTPANE](action, payload) {
        action.commit(types.CALENDAR_TOGGLE_LEFTPANE, payload);
    },

    [types.CALENDAR_RESOURCE_LIST](action, payload: { comCode: string | null,  itemStore: 'FAVORITE' | 'EWS' | 'DB' }) {
        var { comCode, itemStore } = payload;

        if(comCode == null)
            return;

        var listPromise: Promise<reserve.ResourceItemBase[]>[] = [
            api.resource.getResources(comCode, itemStore)
        ];

        if(itemStore === 'EWS' || itemStore === 'DB') {
            listPromise.push(api.resource.getResourceGroups(comCode, itemStore));
        } 

        Promise.all(listPromise).then(res => {            
            switch(itemStore) {                
                case 'DB':
                    action.commit(types.DB_RESOURCEGROUPS_LOAD, [...res[1], ...res[0] ]);
                    break;
                case 'EWS':
                    action.commit(types.EWS_RESOURCEGROUPS_LOAD, [...res[1], ...res[0] ]);
                    break;
                default:
                    action.commit(types.CALENDAR_RESOURCEBOOKMARK_LIST, res[0]);
                    break;               
            }
        });    
    },

    [types.EWS_RESOURCEGROUPS_LOAD](action) {
        return api.resourceEws.getEwsResources()
            .then(res => {                                                
                action.commit(types.EWS_RESOURCEGROUPS_LOAD, res);
                return res;
            });
    },    

    [types.DB_RESOURCEGROUPS_LOAD](action, payload: string) {
        return api.resourceDb.getDbResources(payload).then(res => {            
            action.commit(types.DB_RESOURCEGROUPS_LOAD, res);
            return res;
        });
    },

    [types.TOGGLE_RESOURCE_SELECT](action, payload: { category: 'EWS' | 'DB' | 'FAVORITE', id: string, select: boolean }) {
        action.commit(types.TOGGLE_RESOURCE_SELECT, payload);                
    },

    [types.TOGGLE_RESOURCEGROUP_SELECT](action, payload: { category: 'EWS' | 'DB' | 'FAVORITE', id: string, select: boolean }) {
        action.commit(types.TOGGLE_RESOURCEGROUP_SELECT, payload);
    },

    [types.CALENDAR_MONTH_MATRIX_LOAD](action) {
        action.commit(types.CALENDAR_MONTH_MATRIX_LOAD);
    },

    [types.CALENDAR_WEEK_MATRIX_LOAD](action) {
        action.commit(types.CALENDAR_WEEK_MATRIX_LOAD);
    },

    [types.CALENDAR_DATE_MATRIX_LOAD](action) {
        action.commit(types.CALENDAR_DATE_MATRIX_LOAD);
    },

    [types.CALENDAR_EVENTLIST_LOAD](action, forceViewMode?: EnumCalendarViewType) {
        action.state.isLoading = true;
        const { date } = action.state.currentDate;        
        
        var promiseArr: Promise<any>[] = [];
        var events = [] as reserve.ItemList<reserve.CalendarItem>;
        var selectedResources = action.state.selectedResources.filter(r => !reserveHelper.isHeaderType(r.resourceType))
        
        const start = IEDate(getCurrentStartDate(date, action.state.monthDate, forceViewMode || action.state.calendarMode, action));
        const end = IEDate(getCurrentEndDate(date, action.state.monthDate, forceViewMode || action.state.calendarMode, action));
        end.setDate(end.getDate() + 1);                       

        let ewsResources = selectedResources.filter(r => r.itemStore === 'EWS');
        let dbResources = selectedResources.filter(r => r.itemStore === 'DB');

        dbResources.forEach(r => {
            promiseArr.push(api.resourceDb.getCalendarEventList({
                FolderID: r.folderId,
                StartDate: uGetDateFormat('yyyy-MM-DDT00:00:00', start),
                EndDate: uGetDateFormat('yyyy-MM-DDT00:00:00', end)                                                
            }))
        })

        if(ewsResources.length > 0) {            
            promiseArr.push(api.resourceEws.getResourceEventListWithRecurrence({
                IpUsers: [ ...ewsResources.map(r => r.address) ],
                EwsFolderIDArray: [ ...ewsResources.map(r => r.folderId) ],
                StartDate: uGetDateFormat('yyyy-MM-DDT00:00:00', start),
                EndDate: uGetDateFormat('yyyy-MM-DDT00:00:00', end)                
            }).then(res => {
                events = linkEwsEventToResource(ewsResources, events, res);     
                return res;     
            }));
        }            
       
        return Promise.all(promiseArr).then(res => {
            for(let i = 0; i < dbResources.length; i++) {         
                var eventResult = res[i];
                events = linkDbEventToResource(dbResources, events, eventResult);
            }    

            events.sort((a, b) =>
                    sortFn(Number(IEDate(a.start)), Number(IEDate(b.start))) ||
                    sortFn(Number(a.dateTimeCreated), Number(b.dateTimeCreated))
                );
            
            action.commit(types.CALENDAR_EVENTLIST_LOAD, events);  
            action.commit(types.MINI_CALENDAR_MONTH_MATRIX_LOAD);
            action.commit(types.CALENDAR_MONTH_MATRIX_LOAD);
            action.commit(types.CALENDAR_WEEK_MATRIX_LOAD);
            action.commit(types.CALENDAR_DATE_MATRIX_LOAD);
            return events;          
        }).finally(() => { 
            action.state.isLoading = false; 
        });
    },

    [types.CALENDAR_DATE_SELECT](action, dateItem) {       
        var reload = !isValidReloadFromChangedDate(dateItem, action.state);        
        action.commit(types.CALENDAR_DATE_SELECT, dateItem);
        action.commit(types.MINI_CALENDAR_MONTH_MATRIX_LOAD);

        if(reload) {
            return Promise.all([
                action.dispatch(types.CALENDAR_DAILY_EVENT_LOAD),
                action.dispatch(types.CALENDAR_EVENTLIST_LOAD)        
            ]);  
        } else {
            action.commit(types.CALENDAR_MONTH_MATRIX_LOAD);
            action.commit(types.CALENDAR_WEEK_MATRIX_LOAD);
            action.commit(types.CALENDAR_DATE_MATRIX_LOAD);
            return action.dispatch(types.CALENDAR_DAILY_EVENT_LOAD);
        }                   
    },

    [types.CALENDAR_DATE_SELECTRELOAD](action, dateItem) {
        action.commit(types.CALENDAR_DATE_SELECT, dateItem);
        action.commit(types.MINI_CALENDAR_MONTH_MATRIX_LOAD);

        return Promise.all([
            action.dispatch(types.CALENDAR_DAILY_EVENT_LOAD),
            action.dispatch(types.CALENDAR_EVENTLIST_LOAD)        
        ]);  
    },

    [types.CALENDAR_EVENT_CREATE](action, payload:{ item: reserve.CalendarItem, itemStore: 'EWS' | 'DB' }) {
        var { item, itemStore } = payload;
        var createPromise: Promise<any> = Promise.resolve();

        if(itemStore === 'EWS') {
            const fields: ewsResource.api.FieldOption[] = [
                //{ key: 'ParentFolderId', value: item.parentId },
                { key: 'Importance', value: item.importance },
                { key: 'EVENT_STATUS', value: item.status },
                { key: 'Subject', value: item.subject },
                { key: 'Location', value: item.location },
                { key: 'Start', value: item.start + 'T' + item.starttime },
                { key: 'End', value: item.end + 'T' + item.endtime },
                { key: 'IsAllDayEvent', value: item.isAllDayEvent },
                { key: 'Body', value: item.body },
                { key: 'RequiredAttendees', value: item.requiredAttendees },
                { key: 'OptionalAttendees', value: item.optionalAttendees },
                { key: 'Recurrence', value: item.recurrence },
                { key: 'Attachments', value: item.attachments },
                { key: 'Resources', value: item.resources },
                { key: 'AttachmentKey', value: item.code },
                { key: 'DelegateUser', value: item.delegateUser },
                { key: 'ReminderMinutesBeforeStart', value: item.reminderMinutesBeforeStart }
            ]
    
            const data: ewsResource.api.DynamicApiOptions = {
                type: 'Calendar',
                parentId: item.parentId,
                fields: fields                               
            };
            
            createPromise = api.ewsCalendar.createEvent(data)                    
        } else {
            createPromise = api.resourceDb.createCalendarEvent(item);
        }        

        return createPromise.then(res => {
            return action.dispatch(types.CALENDAR_EVENTLIST_LOAD);
        })
    },

    [types.CALENDAR_EVENT_UPDATE](action, payload: { item: reserve.CalendarItem, itemStore: 'EWS' | 'DB', masterOccurrence: string | null }) {
        let { item, itemStore, masterOccurrence } = payload;
        var updatePromise: Promise<any> = Promise.resolve();

        if(itemStore === 'EWS') {
            const fields: ewsResource.api.FieldOption[] = [            
                { key: 'Importance', value: item.importance },
                { key: 'EVENT_STATUS', value: item.status },
                { key: 'Subject', value: item.subject },
                { key: 'Location', value: item.location },
                { key: 'Start', value: item.start + 'T' + item.starttime },
                { key: 'End', value: item.end + 'T' + item.endtime },
                { key: 'IsAllDayEvent', value: item.isAllDayEvent },
                { key: 'Body', value: item.body },
                { key: 'RequiredAttendees', value: item.requiredAttendees },
                { key: 'OptionalAttendees', value: item.optionalAttendees },
                { key: 'Recurrence', value: item.recurrence },
                { key: 'Attachments', value: item.attachments },
                { key: 'Resources', value: item.resources },
                { key: 'AttachmentKey', value: item.code },
                { key: 'DelegateUser', value: item.delegateUser },
                { key: 'ReminderMinutesBeforeStart', value: item.reminderMinutesBeforeStart },
            ];
    
            const data: ewsResource.api.DynamicApiOptions = {
                fields: fields,
                id: item.id,
                type: 'Calendar'             
            };
    
            updatePromise = api.ewsCalendar.editEvent(data);               
        } else {
            updatePromise = masterOccurrence === 'master' ?
                api.resourceDb.updateCalendarEvent(item) :
                api.resourceDb.updateRCalendarEvent(item);
        }

        updatePromise.then(res => {
            return action.dispatch(types.CALENDAR_EVENTLIST_LOAD)
        }); 
    },

    [types.CALENDAR_EVENT_EWSCANCEL](action, id: string) {       
        return api.ewsCalendar.cancelEvent(id).then((res) => {
            return action.dispatch(types.CALENDAR_EVENTLIST_LOAD)
        })            
    },

    [types.CALENDAR_EVENT_DBCANCEL](action, item: reserve.CalendarItem) {
        return api.resourceDb.deleteCalendarEvent(item).then(res => {
            return action.dispatch(types.CALENDAR_EVENTLIST_LOAD)
        });
    },

    [types.CALENDAR_DAILY_EVENT_LOAD](action) {
        action.state.isDailyEventLoading = true;
        action.state.resourceDailyEvents = [];
        const currentDate = action.state.currentCalendarDate;
        const { date } = currentDate;

        var promiseArr: Promise<any>[] = [];
        var events = [] as reserve.ItemList<reserve.CalendarItem>;
        var selectedResources = action.state.selectedResources.filter(r => !reserveHelper.isHeaderType(r.resourceType))
                
        let endDate: Date = new Date(date);
        endDate.setDate(endDate.getDate() + 1);

        let ewsResources = selectedResources.filter(r => r.itemStore === 'EWS');
        let dbResources = selectedResources.filter(r => r.itemStore === 'DB');

        dbResources.forEach(r => {
            promiseArr.push(api.resourceDb.getCalendarEventList({
                FolderID: r.folderId,
                StartDate: uGetDateFormat('yyyy-MM-DDT00:00:00', date),
                EndDate: uGetDateFormat('yyyy-MM-DDT00:00:00', endDate)                                                
            }))
        })

        if(ewsResources.length > 0) {            
            promiseArr.push(api.resourceEws.getResourceEventListWithRecurrence({
                IpUsers: [ ...ewsResources.map(r => r.address) ],
                EwsFolderIDArray: [ ...ewsResources.map(r => r.folderId) ],
                StartDate: uGetDateFormat('yyyy-MM-DDT00:00:00', date),
                EndDate: uGetDateFormat('yyyy-MM-DDT00:00:00', endDate)                
            }).then(res => {
                events = linkEwsEventToResource(ewsResources, events, res);     
                return res;     
            }));
        }            

        return Promise.all(promiseArr).then(res => {
            for(let i = 0; i < dbResources.length; i++) {         
                var eventResult = res[i];
                events = linkDbEventToResource(dbResources, events, eventResult);
            }      

            events.sort((a, b) =>
                sortFn(Number(IEDate(a.start)), Number(IEDate(b.start))) ||
                sortFn(Number(a.dateTimeCreated), Number(b.dateTimeCreated))
            );
            
            action.commit(types.CALENDAR_DAILY_EVENT_LOAD, events);
            return events;
        }).finally(() => { action.state.isDailyEventLoading = false; });
    },

    [types.CALENDAR_SEARCH_EVENTS_LOAD](action, searchSet?: reserve.ReserveSearchSet) {
        var set = searchSet || action.state.searchSet;
        action.state.isLoading = true;
        const { date } = action.state.currentDate;
        action.state.searchEvents = [];

        var promiseArr: Promise<any>[] = [];
        var events = [] as reserve.ItemList<reserve.CalendarItem>;
        var selectedResources = action.state.selectedResources.filter(r => !reserveHelper.isHeaderType(r.resourceType));

        selectedResources.forEach(r => {
            if(r.itemStore === 'EWS') {
                promiseArr.push(
                    api.resourceEws.searchCalendarEventList(<reserve.EventSearchRequest>{            
                        startDate: IEDate(set.searchStart),
                        endDate: IEDate(set.searchEnd),
                        organizerName: set.reserverName,
                        subject: set.reserveTitle,
                        keyword: set.reserveContent
                    }, r.address)
                );
            } else {
                promiseArr.push(
                    api.resourceDb.searchCalendarEventList({
                        startDate: IEDate(set.searchStart),
                        endDate: IEDate(set.searchEnd),
                        organizerName: set.reserverName,
                        subject: set.reserveTitle,
                        keyword: set.reserveContent
                    })
                );
            }
        })         

        Promise.all(promiseArr)
            .then(res => {
                for(var i = 0; i < selectedResources.length; i++) {
                    var resource = selectedResources[i];
                    var eventResult = res[i];
    
                    for(var event of eventResult) {
                        const existID = events.find((e) => event.id == e.id && (event.appointmentType != 'Occurrence' && event.appointmentType != 'Exception'));
    
                        if(existID) continue;
    
                        event.color = resource.colorCode == null ? getResourceColor(resource.uniqueId) : resource.colorCode ;
                        event.resourceUpn = resource.address;
                        event.itemStore = resource.itemStore;
                        events.push(event);
                    }
                }
    
                events.sort((a, b) =>
                        sortFn(Number(IEDate(a.start)), Number(IEDate(b.start))) ||
                        sortFn(Number(a.dateTimeCreated), Number(b.dateTimeCreated))
                    );

                action.commit(types.CALENDAR_SEARCH_EVENTS_LOAD, events);
                return events;
            }).finally(() => { action.state.isLoading = false; });
    },

    [types.CALENDAR_SEARCHSET_UPDATE](action, payload: reserve.ReserveSearchSet) {
        action.commit(types.CALENDAR_SEARCHSET_UPDATE, payload);
    },

    [types.TOGGLE_MAIN_LISTVIEW](action, payload: boolean) {
        action.commit(types.TOGGLE_MAIN_LISTVIEW, payload);
    }
};
